import { forwardRef, type ReactNode } from 'react';
import { motion, type Variants } from 'motion/react';
import clsx from 'clsx';

interface SectionProps {
  children: ReactNode;
  className?: string;
  background?: 'paper' | 'cream' | 'ink' | 'accent';
  id?: string;
  animate?: boolean;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Section = forwardRef<HTMLElement, SectionProps>(
  ({ children, className, background = 'paper', id, animate = true }, ref) => {
    const bgClasses = {
      paper: 'bg-paper',
      cream: 'bg-cream',
      ink: 'bg-ink text-paper',
      accent: 'bg-accent text-white',
    };

    const content = (
      <div className="container-marketing">
        {children}
      </div>
    );

    if (!animate) {
      return (
        <section
          ref={ref}
          id={id}
          className={clsx('section-padding', bgClasses[background], className)}
        >
          {content}
        </section>
      );
    }

    return (
      <motion.section
        ref={ref}
        id={id}
        className={clsx('section-padding', bgClasses[background], className)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={containerVariants}
      >
        {content}
      </motion.section>
    );
  }
);

Section.displayName = 'Section';

// Animated wrapper for section content items
export function SectionItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div variants={itemVariants} className={className}>
      {children}
    </motion.div>
  );
}

// Section header with title and optional subtitle
export function SectionHeader({
  title,
  subtitle,
  centered = true,
  className,
}: {
  title: string;
  subtitle?: string;
  centered?: boolean;
  className?: string;
}) {
  return (
    <motion.div
      variants={itemVariants}
      className={clsx(
        'mb-12 md:mb-16',
        centered && 'text-center',
        className
      )}
    >
      <h2 className="font-display text-h2 mb-4">{title}</h2>
      {subtitle && (
        <p className="text-muted text-lg max-w-2xl mx-auto">{subtitle}</p>
      )}
    </motion.div>
  );
}
